import { LitElement, html, TemplateResult, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-sidebar-layout.inner.pcss?inline';
import { whenParsed } from '../../utilities/when-parsed';

/**
 * The Sidebar Layout component.
 * @element 'se-sidebar-layout'
 */
@customElement('se-sidebar-layout')
export class SeSidebarLayout extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });

  private isMobile: boolean;

  /**
   * Changes spacing in the sidebar for when content is a snug fit.
   */
  @property({ type: Boolean, reflect: true, attribute: 'snug-fit' })
  snugFit = false;

  @state() hasDisclaimerSlot: boolean;

  mobileMode() {
    const maxMobileWidth = 768 - 1;
    // Set initial value to isMobile
    this.isMobile = window.innerWidth < maxMobileWidth;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);

    // update isMobile value only when screen width is crossing maxMobileWidth (767)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.mobileMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  async firstUpdated() {
    // This component reads its own light-DOM children. In the critical
    // bundle it upgrades mid-parse, before those children exist, so wait
    // for them. See utilities/when-parsed.ts (SE-1004).
    await whenParsed();

    this.hasDisclaimerSlot = !!this.querySelector('[slot="disclaimer"]');
  }

  render(): TemplateResult {
    return html`
      <div
        class="wrapper
          ${this.isMobile ? 'is-mobile' : 'is-desktop'}
          ${this.snugFit ? 'snug-fit' : null}"
      >
        ${this.isMobile
          ? html`
              <slot name="primary-sidebar"></slot>
              <slot name="main-content"></slot>
              ${this.hasDisclaimerSlot
                ? html`<slot name="disclaimer"></slot>`
                : nothing}
              <slot name="secondary-sidebar"></slot>
            `
          : html`
              <div class="sidebar">
                <slot name="primary-sidebar"></slot>
                <slot name="secondary-sidebar"></slot>
              </div>
              <div class="main-content">
                <slot name="main-content"></slot>
                ${this.hasDisclaimerSlot
                  ? html`<slot name="disclaimer"></slot>`
                  : nothing}
              </div>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-sidebar-layout': SeSidebarLayout;
  }
}
